import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import { RecordingAsset } from 'types';

import { useAllRecordingAssets, useRecordingSettings } from '@kaltura-ott/tvpil-shared';

import { DEFAULT_IMAGE_PARAMS } from 'consts';
import { WidgetProviderData } from 'context/widgetProvider/types';
import { useLoadingPerformanceEvents } from 'hooks';
import { useCheckDataIsEmpty } from 'hooks/common/useCheckDataIsEmpty/useCheckDataIsEmpty';
import { useRhinoErrorReaction } from 'hooks/common/useRhinoErrorReaction';
import { APPLICATION_SPINNER_EVENT_NAME } from 'utils';
import { applyStyleHexToHSL, applyStyles } from 'utils/uiDesignElements';

import { PAGE_SIZE } from '../../constants';
import { COMPONENT_NAME } from '../constants';

function useRecordingsGrid(referenceId: string){
  const settings = useRecordingSettings({
    referenceId,
    ...DEFAULT_IMAGE_PARAMS,
  });
  const originalData = useAllRecordingAssets({ referenceId, pageSize: PAGE_SIZE });
  const shouldShowEmptyContent = useCheckDataIsEmpty(originalData);
  const wrapperRef = useRef(null);
  const shouldSendAppLoadingPerformanceEvent = originalData.isLoading && !originalData.items.length;
  const intl = useIntl();
  const notValidPageTitle = intl.formatMessage({
    id: `${COMPONENT_NAME}.notValidPage`,
    defaultMessage: 'Not a valid page',
  });

  const items: Array<RecordingAsset> = originalData.items.map((recordingItem) => ({
    id: recordingItem.asset.id,
    images: recordingItem.asset.images,
    ...recordingItem,
  }));

  const data: WidgetProviderData<RecordingAsset> = useMemo(() => {
    return {
      items,
      hasNextPage: originalData.hasNextPage,
      isLoading: originalData.isLoading,
      error: originalData.error,
      fetchNextPage: originalData.fetchNextPage,
      refetch: originalData.refetch,
    }
  }, [originalData.isLoading]);


  useEffect(() => {
    function initGridPage() {
      if (!settings) {
        return;
      }

      const node = wrapperRef?.current;

      if (node && settings.branding) {
        applyStyles(node, settings.branding, false, true);
        applyStyleHexToHSL(node, settings.branding.backgroundColor);
      }
    }

    initGridPage();
  }, [settings]);

  useRhinoErrorReaction({ error: originalData.error, refetch: originalData.refetch });

  useLoadingPerformanceEvents(APPLICATION_SPINNER_EVENT_NAME.GRID_PAGE, shouldSendAppLoadingPerformanceEvent);

  return {
    data,
    settings,
    wrapperRef,
    notValidPageTitle,
    shouldShowEmptyContent
  }
}

export { useRecordingsGrid }
